import type { ChatConversation } from '../client';
import { getWidgetLocale, t, tf } from './i18n';

const ITEM_CLASS = 'flix-chat-widget__history-item';
const RECENT_CLASS = 'flix-chat-widget__recent';
const VIEWER_CLASS = 'flix-chat-widget__history-viewer';

// The home screen shows only the newest few conversations, not the whole
// archive — a deliberate product decision (keep the home screen glanceable;
// anything older is out of sight the way it is in the reference designs).
export const RECENT_CONVERSATIONS_LIMIT = 3;

function statusLabel(status: string): string {
  return status === 'open' ? t('statusOpen') : t('statusClosed');
}

// A short, absolute fallback label for a conversation whose title is still
// null (no customer message was ever sent on it) — distinct from the
// relative date shown underneath every row.
function fallbackTitle(createdAt: string): string {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return t('conversationFallbackTitle');
  }

  return date.toLocaleDateString(getWidgetLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function relativeDate(createdAt: string): string {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const startOfDay = (value: Date): number =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();

  const diffDays = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000);

  if (diffDays === 0) {
    return t('relToday');
  }

  if (diffDays === 1) {
    return t('relYesterday');
  }

  if (diffDays > 1 && diffDays < 7) {
    return tf('relDaysAgo', { n: diffDays });
  }

  return date.toLocaleDateString(getWidgetLocale(), { month: 'short', day: 'numeric' });
}

function createItem(
  conversation: ChatConversation,
  unread: boolean,
  onSelect: (conversationId: string) => void,
): HTMLElement {
  const item = document.createElement('button');
  item.type = 'button';
  item.className = unread ? `${ITEM_CLASS} ${ITEM_CLASS}--unread` : ITEM_CLASS;
  item.addEventListener('click', () => onSelect(conversation.id));

  const main = document.createElement('div');
  main.className = `${ITEM_CLASS}-main`;

  const titleRow = document.createElement('span');
  titleRow.className = `${ITEM_CLASS}-title-row`;

  if (unread) {
    const dot = document.createElement('span');
    dot.className = `${ITEM_CLASS}-unread-dot`;
    dot.setAttribute('aria-label', t('historyUnreadAria'));
    titleRow.append(dot);
  }

  const title = document.createElement('span');
  title.className = `${ITEM_CLASS}-title`;
  title.textContent = conversation.title ?? fallbackTitle(conversation.createdAt);
  titleRow.append(title);

  const date = document.createElement('span');
  date.className = `${ITEM_CLASS}-date`;
  date.textContent = relativeDate(conversation.createdAt);

  main.append(titleRow, date);

  const status = document.createElement('span');
  status.className = `${ITEM_CLASS}-status ${ITEM_CLASS}-status--${conversation.status}`;
  status.textContent = statusLabel(conversation.status);

  item.append(main, status);
  return item;
}

/**
 * The "recent conversations" block shown on the widget's home screen,
 * directly under the Start new chat button — at most
 * RECENT_CONVERSATIONS_LIMIT rows, newest first (the API already returns
 * them in that order). Returns null when there's nothing to show, so the
 * home screen stays exactly as it was for a first-time customer.
 */
export function createRecentConversationsSection(
  conversations: ChatConversation[],
  isUnread: (conversationId: string) => boolean,
  onSelect: (conversationId: string) => void,
): HTMLElement | null {
  if (conversations.length === 0) {
    return null;
  }

  // The API already orders by recent activity, which normally makes this a
  // no-op — but a reply on a conversation that isn't otherwise the most
  // recent one (a network hiccup delaying the request, an older ticket
  // reopened by staff) should still float to the very top: unread trumps
  // recency here, same as the launcher badge always outranks other teaser
  // states.
  const ordered = [...conversations].sort((a, b) => Number(isUnread(b.id)) - Number(isUnread(a.id)));
  const recent = ordered.slice(0, RECENT_CONVERSATIONS_LIMIT);

  const section = document.createElement('div');
  section.className = RECENT_CLASS;

  const heading = document.createElement('div');
  heading.className = `${RECENT_CLASS}-title`;
  heading.textContent = t('recentConversationsTitle');

  const list = document.createElement('div');
  list.className = `${RECENT_CLASS}-list`;

  for (const conversation of recent) {
    list.append(createItem(conversation, isUnread(conversation.id), onSelect));
  }

  section.append(heading, list);
  return section;
}

// The read-only view for a single past conversation: a small fixed banner
// (label + its own back control) wrapped around whatever content element is
// passed in — widget.ts feeds it the same messageList.element used for the
// live conversation, just rendered with a different, read-only-sourced
// message array. No new message rendering code needed here.
export function createHistoryViewer(onBack: () => void, content: HTMLElement): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = VIEWER_CLASS;

  const banner = document.createElement('div');
  banner.className = `${VIEWER_CLASS}-banner`;

  const label = document.createElement('span');
  label.textContent = t('viewingPast');

  const backButton = document.createElement('button');
  backButton.type = 'button';
  backButton.className = `${VIEWER_CLASS}-banner-back`;
  backButton.textContent = t('back');
  backButton.addEventListener('click', () => onBack());

  banner.append(label, backButton);
  wrapper.append(banner, content);

  return wrapper;
}
